/**
 * RateInfoBadge — small (i) icon shown next to any MYR value that was
 * computed from USD × admin-set exchange rate. On hover it explains the
 * calculation so users never wonder "kenapa RM value ni?".
 *
 * Usage:
 *   <RateInfoBadge usd={3777.38} rate={4.2} period="2026-06" />
 *   → renders "(i)" that reveals:
 *     "RM 15,865.00 = $3,777.38 × 4.20 (Jun 2026, admin-set)"
 */
import React from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const MONTH_NAMES = ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogo', 'Sep', 'Okt', 'Nov', 'Dis'];

function fmtPeriod(period) {
  const s = String(period || '');
  if (s.length < 7 || s[4] !== '-') return s;
  const y = s.substring(0, 4);
  const mIdx = parseInt(s.substring(5, 7), 10) - 1;
  if (mIdx < 0 || mIdx > 11) return s;
  return `${MONTH_NAMES[mIdx]} ${y}`;
}

function fmtRM(n) {
  return `RM ${Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtUSD(n) {
  return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function RateInfoBadge({ usd, rate, period, size = 12, className = '' }) {
  if (usd === undefined || rate === undefined) return null;
  const myr = Number(usd || 0) * Number(rate || 0);
  const rateLabel = Number(rate || 0).toFixed(2);
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid="rate-info-badge"
            className={`inline-flex items-center justify-center rounded-full text-[#9CA3AF] hover:text-white transition-colors ${className}`}
            aria-label="Rate info"
          >
            <Info size={size} strokeWidth={2} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-[#111827] border border-[#1F2937] text-white max-w-xs">
          <div className="text-[11px] font-mono tabular-nums space-y-1 py-1">
            <div className="text-[#9CA3AF] uppercase tracking-wider text-[9px] mb-1">Kiraan USD → MYR</div>
            <div className="text-white font-bold">{fmtRM(myr)}</div>
            <div className="text-[#6B7280]">= {fmtUSD(usd)} × {rateLabel}</div>
            {period ? (
              <div className="text-[#6B7280] mt-1 pt-1 border-t border-[#1F2937]">
                Rate {fmtPeriod(period)} · set oleh admin
              </div>
            ) : null}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default RateInfoBadge;
